import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { type GuestLink } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableEmail } from "../components/CopyableId";
import { ReissueButton } from "../components/ReissueButton";
import { StatusTag } from "../components/Tags";

export function GuestUsersPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "users", label: "Users", active: false, onSelect: () => navigate("/support/users") },
    { id: "guests", label: "Guest users", active: true, onSelect: () => navigate("/support/guest-users") },
    { id: "broken", label: "Broken users", active: false, onSelect: () => navigate("/support/broken-users") },
  ]);

  const [params, setParams] = useSearchParams();
  const email = params.get("email") ?? "";
  const [input, setInput] = useState(email);
  const [results, setResults] = useState<GuestLink[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setInput(email); }, [email]);

  // No (or too-short) search term = "latest" mode: the backend returns the 10 most recent links.
  const latestMode = email.length < 2;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.searchShareLinks(latestMode ? undefined : email).then(
      (rows) => { if (!cancelled) setResults(rows); },
      (e) => { if (!cancelled) { setError(`${e}`); setResults(null); } },
    ).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, email, latestMode]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = input.trim();
    if (v.length < 2) { setError("Type at least 2 characters of the email to search."); return; }
    setParams((prev) => { const next = new URLSearchParams(prev); next.set("email", v); return next; });
  }

  function clearSearch() {
    setInput("");
    setParams((prev) => { const next = new URLSearchParams(prev); next.delete("email"); return next; });
  }

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Users · Guest users</div>
        <h1>Guest users</h1>
        <p>
          Search guest links (share links) by recipient email substring. See their status and the
          project — and its workspace / organization — they grant access to. Reissuing re-sends the same
          email and does not change the expiry.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Find guest link
          <span className="hint">
            {latestMode ? "showing the latest 10 — search to filter" : "substring match on recipient email"}
          </span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={submit}>
            <input
              type="search"
              placeholder="guest@example.com"
              spellCheck={false}
              autoComplete="off"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button className={"btn btn-primary" + (loading ? " loading" : "")} disabled={loading || input.trim().length < 2}>
              <span className="glyph" /> Search
            </button>
            {!latestMode && (
              <button type="button" className="btn btn-ghost" onClick={clearSearch} title="Back to the latest 10">
                Clear
              </button>
            )}
          </form>

          {error && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{error}</span>
            </div>
          )}

          {results && (
            <div style={{ marginTop: 18, border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Project</th>
                    <th>Workspace</th>
                    <th>Organization</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={6}>
                        {latestMode ? "no guest links yet" : <>no guest links matched <span className="mono">{email}</span></>}
                      </td>
                    </tr>
                  )}
                  {results.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <CopyableEmail
                          email={g.recipientEmail}
                          linkTo={g.recipientEmail
                            ? `/support/users?q=${encodeURIComponent(g.recipientEmail)}&open=1`
                            : undefined}
                        />
                      </td>
                      <td><StatusTag status={g.status} /></td>
                      <td>{g.projectName ?? <span className="faint">(unknown)</span>}</td>
                      <td>{g.workspaceName ?? <span className="faint">—</span>}</td>
                      <td>
                        {g.organizationId ? (
                          <Link
                            to={`/support/organizations?org=${encodeURIComponent(g.organizationId)}`}
                            style={{ color: "var(--cyan)" }}
                            title="Open this organization"
                          >
                            {g.organizationName ?? "(unnamed)"}
                          </Link>
                        ) : (
                          g.organizationName ?? <span className="faint">—</span>
                        )}
                      </td>
                      <td className="actions">
                        <ReissueButton onReissue={() => api.resendShareLink(g.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
